import React from 'react';
import { Card, CardContent, Box, Typography, Divider } from '@mui/material';
import ScrollArea from './ScrollArea';

/*
  AppCard: standard soft content card with an optional header row.
  Props:
   - title: string (optional card title)
   - icon: React node (optional, rendered muted before the title)
   - action: React node (optional, right-aligned header content)
   - children: card body
   - sx / contentSx: style overrides for Card / CardContent
   - scroll: when true, the body fills the card and scrolls internally with a
       uniform custom scroll thumb (see ScrollArea); scrollSx styles the scroller
   - divider: show a divider under the header row (default true)
*/

export default function AppCard({ title, icon, action, children, sx, contentSx, scroll = false, scrollSx, divider = true }) {
  const hasHeader = Boolean(title || icon || action);
  return (
    <Card elevation={2} sx={{ height: '100%', overflow: 'visible', ...sx }}>
      {hasHeader && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 2,
              py: 0.5,
              // Fixed header height so the divider sits at the same offset on
              // every card, regardless of how tall the action control is.
              minHeight: 56,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
            >
              {icon && (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}
                >
                  {icon}
                </Box>
              )}
              {title}
            </Typography>
            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
          </Box>
          {divider && <Divider />}
        </>
      )}
      {scroll ? (
        <CardContent
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '&:last-child': { pb: 0 },
            ...contentSx,
          }}
        >
          <ScrollArea sx={scrollSx}>{children}</ScrollArea>
        </CardContent>
      ) : (
        <CardContent sx={contentSx}>{children}</CardContent>
      )}
    </Card>
  );
}
